// src/pages/CheckoutPage.js

import React, {
  useState,
  useEffect,
} from "react";

import {
  createOrder,
  createRazorpayOrder,
  verifyRazorpayPayment,
} from "../api/axios";

import {
  useAuth,
} from "../components/AuthContext";

import {
  useCart,
} from "../components/CartContext";

import {
  useNavigate,
} from "react-router-dom";

import "../styles/CheckoutPage.css";

const CheckoutPage = () => {

  /* =========================================================
  CONTEXT
  ========================================================= */

  const { user } =
    useAuth();

  const {
    cart,
    clearCart,
  } = useCart();

  const navigate =
    useNavigate();

  /* =========================================================
  STATES
  ========================================================= */

  const [loading,
    setLoading] =
    useState(false);

  const [success,
    setSuccess] =
    useState(false);

  const [orderDetails,
    setOrderDetails] =
    useState(null);

  const [paymentMethod,
    setPaymentMethod] =
    useState("COD");

  const [shippingInfo,
    setShippingInfo] =
    useState({

      fullName:"",

      phoneNumber:"",

      address:"",

      city:"",

      state:"",

      postalCode:"",

      country:"India",
    });

  /* =========================================================
  PREFILL USER DATA
  ========================================================= */

  useEffect(() => {

    if (user) {

      setShippingInfo((prev) => ({

        ...prev,

        fullName:
          user.name || "",

        phoneNumber:
          user.phone || "",
      }));
    }

  }, [user]);

  /* =========================================================
  EMPTY CART REDIRECT
  ========================================================= */

  useEffect(() => {

    if (cart.length === 0) {

      navigate("/cart");
    }

  }, [cart, navigate]);

  /* =========================================================
  PRICE
  ========================================================= */

  const getItemPrice =
    (item) =>

      item.discountPrice &&
      item.discountPrice > 0

        ? item.discountPrice

        : item.price;

  const itemsPrice =
    cart.reduce(

      (acc, item) =>

        acc +

        getItemPrice(item) *

        (item.quantity || 1),

      0
    );

  /* GST */

  const taxPrice =
    +(
      itemsPrice * 0.18
    ).toFixed(2);

  /* SHIPPING */

  const shippingPrice =

    itemsPrice > 1000

      ? 0

      : itemsPrice > 0

      ? 100

      : 0;

  /* TOTAL */

  const totalPrice =
    +(
      itemsPrice +
      taxPrice +
      shippingPrice
    ).toFixed(2);

  /* =========================================================
  FACEBOOK PIXEL
  ========================================================= */

  useEffect(() => {

    if (
      window.fbq &&
      cart.length > 0
    ) {

      window.fbq(
        "track",
        "InitiateCheckout",
        {

          value:Number(
            totalPrice
          ),

          currency:"INR",

          num_items:
            cart.length,

          content_ids:
            cart.map(
              (item) =>
                item._id
            ),

          content_type:
            "product",
        }
      );
    }

  }, [cart, totalPrice]);

  /* =========================================================
  ORDER ITEMS
  ========================================================= */

  const buildOrderItems =
    () =>

      cart.map((item) => ({

        product:item._id,

        name:item.name,

        fullName:
          item.fullName ||
          item.name,

        variant:
          item.variant || "",

        image:
          item.imageUrls?.[0] ||
          item.image ||

          "https://via.placeholder.com/400",

        qty:
          item.quantity || 1,

        price:
          getItemPrice(item),
      }));

  /* =========================================================
  LOAD RAZORPAY
  ========================================================= */

  const loadRazorpay =
    () =>

      new Promise(
        (resolve) => {

          if (
            window.Razorpay
          ) {

            return resolve(true);
          }

          const script =
            document.createElement(
              "script"
            );

          script.src =
            "https://checkout.razorpay.com/v1/checkout.js";

          script.onload =
            () => resolve(true);

          script.onerror =
            () => resolve(false);

          document.body.appendChild(
            script
          );
        }
      );

  /* =========================================================
  ONLINE PAYMENT
  ========================================================= */

  const startRazorpayPayment =
    async () => {

      const loaded =
        await loadRazorpay();

      if (!loaded) {

        return alert(
          "Razorpay failed to load"
        );
      }

      try {

        setLoading(true);

        const rpRes =
          await createRazorpayOrder({

            userId:
              user?._id,

            orderItems:
              buildOrderItems(),

            shippingAddress:
              shippingInfo,

            itemsPrice,

            taxPrice,

            shippingPrice,

            totalPrice,
          });

        const {
          razorpayOrder,
          orderId,
        } =
          rpRes.data;

        const options = {

          key:
            process.env
              .REACT_APP_RAZORPAY_KEY,

          amount:
            razorpayOrder.amount,

          currency:"INR",

          name:"SAC MTB",

          description:
            "Premium SAC Order",

          image:
            "/logo192.png",

          order_id:
            razorpayOrder.id,

          theme:{
            color:"#9dff00",
          },

          handler:
            async (
              response
            ) => {

              const verifyRes =
                await verifyRazorpayPayment({

                  ...response,

                  orderId,
                });

              if (
                !verifyRes.data.success
              ) {

                alert(
                  "Payment verification failed"
                );

                return;
              }

              /* PURCHASE PIXEL */

              if (
                window.fbq
              ) {

                window.fbq(
                  "track",
                  "Purchase",
                  {

                    value:
                      Number(totalPrice),

                    currency:
                      "INR",

                    content_ids:
                      cart.map(
                        (i) =>
                          i._id
                      ),

                    content_type:
                      "product",

                    num_items:
                      cart.length,
                  }
                );
              }

              setOrderDetails(
                verifyRes.data.order
              );

              setSuccess(true);

              clearCart();

              setTimeout(
                () =>
                  navigate("/"),

                3000
              );
            },
        };

        new window.Razorpay(
          options
        ).open();

      } catch (err) {

        console.error(err);

        alert(
          "Payment failed. Please try again."
        );

      } finally {

        setLoading(false);
      }
    };

  /* =========================================================
  CASH ON DELIVERY
  ========================================================= */

  const submitCODOrder =
    async () => {

      try {

        setLoading(true);

        const { data } =
          await createOrder({

            orderItems:
              buildOrderItems(),

            shippingAddress:
              shippingInfo,

            paymentMethod:
              "COD",

            itemsPrice,

            taxPrice,

            shippingPrice,

            totalPrice,
          });

        /* PURCHASE PIXEL */

        if (
          window.fbq
        ) {

          window.fbq(
            "track",
            "Purchase",
            {

              value:
                Number(totalPrice),

              currency:
                "INR",

              content_ids:
                cart.map(
                  (i) =>
                    i._id
                ),

              content_type:
                "product",

              num_items:
                cart.length,
            }
          );
        }

        setOrderDetails(
          data.order
        );

        setSuccess(true);

        clearCart();

        setTimeout(
          () =>
            navigate("/"),

          3000
        );

      } catch (err) {

        console.error(err);

        alert(
          "Order failed"
        );

      } finally {

        setLoading(false);
      }
    };

  /* =========================================================
  VALIDATION
  ========================================================= */

  const validateForm =
    () => {

      if (!user) {

        alert(
          "Please login first"
        );

        return false;
      }

      if (

        !shippingInfo.fullName ||

        !shippingInfo.phoneNumber ||

        !shippingInfo.address ||

        !shippingInfo.city ||

        !shippingInfo.state ||

        !shippingInfo.postalCode
      ) {

        alert(
          "Please fill all fields"
        );

        return false;
      }

      if (

        !/^[6-9]\d{9}$/

          .test(
            shippingInfo.phoneNumber
          )
      ) {

        alert(
          "Invalid phone number"
        );

        return false;
      }

      return true;
    };

  /* =========================================================
  CHECKOUT
  ========================================================= */

  const handleCheckout =
    () => {

      if (
        !validateForm()
      ) return;

      paymentMethod === "COD"

        ? submitCODOrder()

        : startRazorpayPayment();
    };

  /* =========================================================
  SUCCESS
  ========================================================= */

  if (success) {

    return (

      <div className="sacCheckoutPage">

        <div className="sacCheckoutOverlay"></div>

        <img
          src="https://i.ibb.co/cS1d9nfp/sac-mtb-about-us-bicycle-nature.png"
          alt="SAC"
          className="sacCheckoutBg"
        />

        <div className="sacCheckoutGlow"></div>

        <div className="sacSuccessCard">

          <h1>

            🎉 ORDER CONFIRMED

          </h1>

          <p>

            Your premium SAC MTB
            order has been placed
            successfully.

          </p>

          <h3>

            Order ID:
            {" "}
            {orderDetails?._id}

          </h3>

          <strong>

            ₹
            {totalPrice.toLocaleString()}

          </strong>

          <p className="sacRedirectText">

            Redirecting to homepage...

          </p>

        </div>

      </div>
    );
  }

  /* =========================================================
  UI
  ========================================================= */

  return (

    <div className="sacCheckoutPage">

      {/* BG */}

      <div className="sacCheckoutOverlay"></div>

      <img
        src="https://i.ibb.co/cS1d9nfp/sac-mtb-about-us-bicycle-nature.png"
        alt="SAC"
        className="sacCheckoutBg"
      />

      <div className="sacCheckoutGlow"></div>

      <div className="sacCheckoutWrapper">

        {/* =====================================================
        FORM
        ===================================================== */}

        <div className="sacCheckoutForm">

          <h1>

            CHECKOUT

          </h1>

          <p>

            Complete your premium
            SAC MTB experience.

          </p>

          {/* INPUTS */}

          {[
            {
              key:"fullName",
              label:"Full Name",
            },

            {
              key:"phoneNumber",
              label:"Phone Number",
            },

            {
              key:"address",
              label:"Address",
            },

            {
              key:"city",
              label:"City",
            },

            {
              key:"state",
              label:"State",
            },

            {
              key:"postalCode",
              label:"Postal Code",
            },

          ].map((field) => (

            <input
              key={field.key}

              placeholder={
                field.label
              }

              value={
                shippingInfo[
                  field.key
                ]
              }

              onChange={(e) =>
                setShippingInfo({

                  ...shippingInfo,

                  [field.key]:
                    e.target.value,
                })
              }
            />
          ))}

          {/* =====================================================
          PAYMENT
          ===================================================== */}

          <div className="sacPaymentBox">

            <h3>

              Payment Method

            </h3>

            <label>

              <input
                type="radio"

                checked={
                  paymentMethod ===
                  "COD"
                }

                onChange={() =>
                  setPaymentMethod(
                    "COD"
                  )
                }
              />

              Cash on Delivery

            </label>

            <label>

              <input
                type="radio"

                checked={
                  paymentMethod ===
                  "Online"
                }

                onChange={() =>
                  setPaymentMethod(
                    "Online"
                  )
                }
              />

              Pay Online

            </label>

          </div>

          {/* =====================================================
          BUTTON
          ===================================================== */}

          <button
            className="sacCheckoutBtn"

            onClick={
              handleCheckout
            }

            disabled={
              loading ||
              cart.length === 0
            }
          >

            {loading

              ? "Processing Payment..."

              : "Place Order"}

          </button>

        </div>

        {/* =====================================================
        SUMMARY
        ===================================================== */}

        <div className="sacOrderSummary">

          <h2>

            ORDER SUMMARY

          </h2>

          {cart.map((item) => (

            <div
              key={`${item._id}-${item.variant}`}

              className="sacOrderItem"
            >

              <img
                src={
                  item.imageUrls?.[0] ||

                  item.image ||

                  "https://via.placeholder.com/400"
                }

                alt={item.name}
              />

              <div>

                <h4>

                  {

                    item.name?.length > 55

                    ? item.name.slice(0,55) + "..."

                    : item.name
                  }

                </h4>

                {item.variant && (

                  <p>

                    Variant:
                    {" "}
                    {item.variant}

                  </p>
                )}

                <span>

                  Qty:
                  {" "}
                  {item.quantity}

                </span>

              </div>

              <strong>

                ₹
                {(
                  getItemPrice(item) *

                  item.quantity
                ).toLocaleString()}

              </strong>

            </div>
          ))}

          {/* =====================================================
          PRICE
          ===================================================== */}

          <div className="sacPriceBox">

            <div>

              <span>
                Subtotal
              </span>

              <strong>

                ₹
                {itemsPrice.toLocaleString()}

              </strong>

            </div>

            <div>

              <span>
                GST (18%)
              </span>

              <strong>

                ₹
                {taxPrice.toLocaleString()}

              </strong>

            </div>

            <div>

              <span>
                Shipping
              </span>

              <strong>

                ₹
                {shippingPrice.toLocaleString()}

              </strong>

            </div>

            <div className="sacGrandTotal">

              <span>
                Total
              </span>

              <strong>

                ₹
                {totalPrice.toLocaleString()}

              </strong>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default CheckoutPage;